'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import {
    IconLoader2,
    IconBrandWhatsapp,
    IconBrandTelegram,
    IconDeviceMobile,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { OtpInput } from '@/components/ui/otp-input';

interface OtpStepProps {
    otpCode: string;
    setOtpCode: (code: string) => void;
    availableMethods: string[];
    selectedMethod: string;
    setSelectedMethod: (method: string) => void;
    isLoading: boolean;
    error: string | null;
    isSendingCode: boolean;
    countdowns: Record<string, number>;
    methodStatuses: Record<string, string>;
    onSubmit: () => Promise<void>;
    onResendCode: (method?: string) => Promise<void>;
}

export function OtpStep({
    otpCode,
    setOtpCode,
    availableMethods,
    selectedMethod,
    setSelectedMethod,
    isLoading,
    error,
    isSendingCode,
    countdowns,
    methodStatuses,
    onSubmit,
    onResendCode,
}: OtpStepProps) {
    const t = useTranslations('auth');
    const tSecurity = useTranslations('settings.security');
    const tCommon = useTranslations('common');

    const currentCountdown = countdowns[selectedMethod] || 0;

    return (
        <div className="flex flex-col gap-8">
            {error && (
                <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/50 rounded-md">
                    {error}
                </div>
            )}

            {availableMethods.length > 1 && (
                <div className="space-y-6">
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">
                        {tSecurity('selectMethod')}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setSelectedMethod('totp')}
                            className={cn(
                                "h-14 w-full gap-2 transition-all flex items-center justify-center border-2",
                                selectedMethod === 'totp'
                                    ? "border-primary bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
                                    : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted"
                            )}
                            type="button"
                        >
                            <IconDeviceMobile className="size-4 shrink-0" />
                            <span className="text-xs sm:text-sm font-semibold text-center leading-tight">{tSecurity('totp')}</span>
                        </Button>
                        {availableMethods.includes('whatsapp') && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    if (selectedMethod !== 'whatsapp') {
                                        setSelectedMethod('whatsapp');
                                        const whatsappCountdown = countdowns['whatsapp'] || 0;
                                        if (whatsappCountdown === 0 && methodStatuses['whatsapp'] === 'connected') onResendCode('whatsapp');
                                    }
                                }}
                                className={cn(
                                    "h-14 w-full gap-2 transition-all flex flex-col items-center justify-center border-2 px-2 relative overflow-hidden",
                                    selectedMethod === 'whatsapp'
                                        ? "border-primary bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
                                        : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted"
                                )}
                                type="button"
                                disabled={methodStatuses['whatsapp'] !== 'connected'}
                            >
                                <div className="flex items-center gap-1.5 justify-center w-full">
                                    <IconBrandWhatsapp className="size-4 shrink-0" />
                                    <span className="text-xs sm:text-sm font-semibold truncate">{tSecurity('whatsapp')}</span>
                                </div>
                                {methodStatuses['whatsapp'] !== 'connected' ? (
                                    <span className="text-[10px] text-red-500 font-medium">Offline</span>
                                ) : (countdowns['whatsapp'] > 0 && (
                                    <span className="text-[10px] text-primary font-medium">{countdowns['whatsapp']}s</span>
                                ))}
                            </Button>
                        )}
                        {availableMethods.includes('telegram') && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    if (selectedMethod !== 'telegram') {
                                        setSelectedMethod('telegram');
                                        const telegramCountdown = countdowns['telegram'] || 0;
                                        if (telegramCountdown === 0) onResendCode('telegram');
                                    }
                                }}
                                className={cn(
                                    "h-14 w-full gap-2 transition-all flex flex-col items-center justify-center border-2 px-2 relative overflow-hidden",
                                    selectedMethod === 'telegram'
                                        ? "border-primary bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
                                        : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted"
                                )}
                                type="button"
                            >
                                <div className="flex items-center gap-1.5 justify-center w-full">
                                    <IconBrandTelegram className="size-4 shrink-0" />
                                    <span className="text-xs sm:text-sm font-semibold truncate">{tSecurity('telegram')}</span>
                                </div>
                                {countdowns['telegram'] > 0 && (
                                    <span className="text-[10px] text-primary font-medium">{countdowns['telegram']}s</span>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <div className="space-y-6 py-4 text-center">
                <label className="text-sm font-medium text-foreground/80">
                    {selectedMethod === 'totp'
                        ? tSecurity('enterAuthenticatorCode')
                        : selectedMethod === 'telegram'
                            ? tSecurity('enterTelegramCode')
                            : tSecurity('enterWhatsappCode')
                    }
                </label>
                <div className="flex justify-center">
                    <OtpInput
                        value={otpCode}
                        onChange={setOtpCode}
                        disabled={isLoading}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-4 mt-4">
                <Button
                    onClick={onSubmit}
                    disabled={isLoading || otpCode.length < 6}
                    className="w-full h-11 text-base font-semibold"
                    type="button"
                >
                    {isLoading ? (
                        <>
                            <IconLoader2 className="size-4 mr-2 animate-spin" />
                            {t('verifying')}
                        </>
                    ) : (
                        t('verify')
                    )}
                </Button>

                <div className="h-9 flex items-center justify-center">
                    {(selectedMethod === 'whatsapp' || selectedMethod === 'telegram') ? (
                        <Button
                            variant="ghost"
                            onClick={() => onResendCode()}
                            disabled={isSendingCode || currentCountdown > 0}
                            className="h-full text-xs text-muted-foreground hover:text-foreground"
                            type="button"
                        >
                            {isSendingCode ? (
                                <>
                                    <IconLoader2 className="size-3 mr-2 animate-spin" />
                                    {tCommon('loading')}
                                </>
                            ) : currentCountdown > 0 ? (
                                tSecurity('resendIn', { seconds: currentCountdown })
                            ) : (
                                tSecurity('resendPrompt')
                            )}
                        </Button>
                    ) : (
                        <div className="h-9" />
                    )}
                </div>
            </div>
        </div>
    );
}
